// src/pages/Opportunites.tsx
import React, { useState, useEffect, useMemo } from "react"
import Header from "../components/Header"
import Footer from "../components/Footer"
import { db, auth } from "../firebase"
import {
  doc,
  onSnapshot,
  updateDoc,
  arrayRemove,
  getDoc
} from "firebase/firestore"
import "tippy.js/dist/tippy.css"
import Tippy from "@tippyjs/react"
import "../styles/Modal.css"
import "../styles/Opportunites.css"
import OpportuniteModal from "../components/OpportunitesModal"
import TypeDemandeModal from "../components/TypeDemandeModal"

import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useNavigate, useLocation } from "react-router-dom"
import { usePageTitle } from "../hooks/usePageTitle"
import { logUserAction } from "../utils/logUtils"
import { onAuthStateChanged } from "firebase/auth"
import BillingRecapTable from "../components/BillingRecapTable"
import { getUserRole } from "../utils/authUtils"
import { useDemoContext } from "../context/DemoContext"

interface FirestoreOpportunity {
  companyId: string;
  contact: string;
  type: string;
  amount: number;
  date: string;
  createdAt?: string;
  qte?: number;
  pu?: number;
  remise?: number;
  ttc?: number;
  description?: string;
  status?: string;
}

interface CompaniesData {
  [companyName: string]: {
    opportunities?: FirestoreOpportunity[]
    principalEmail?: string
    principalPhone?: string
    principalContact?: string
    principalFirstName?: string
    principalPosition?: string
    principalGender?: string
    [key: string]: any
  }
}

const Opportunites: React.FC = () => {
  usePageTitle("Mes Demandes");
  const { companiesDocId, isDemoMode } = useDemoContext()
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [preselectedCompany, setPreselectedCompany] = useState<string | null>(null);
  const [contactModal, setContactModal] = useState<{
    name: string;
    phone?: string;
    email?: string;
    fonction?: string;
    gender?: string;
    principalContact?: string;
    principalFirstName?: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMonth, setFilterMonth] = useState((new Date().getMonth() + 1).toString());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [sortBy, setSortBy] = useState<"date" | "amount" | "alphabetical">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [opportunities, setOpportunities] = useState<FirestoreOpportunity[]>([]);
  const [editingOpportunity, setEditingOpportunity] = useState<FirestoreOpportunity | null>(null);
  const [companiesMap, setCompaniesMap] = useState<CompaniesData>({});
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([]);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState<FirestoreOpportunity | null>(null);
  const [showTypeDemandeModal, setShowTypeDemandeModal] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Fonction pour recharger les types depuis Firestore
  const reloadOpportunityTypes = async () => {
    try {
      const docRef = doc(db, "prospects", companiesDocId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as CompaniesData;
        const types = (data.opportunityTypes as string[]) || [];
        console.log("Types rechargés depuis Firestore:", types);
        setOpportunityTypes(types);
      }
    } catch (error) {
      console.error("Erreur lors du rechargement des types:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  // Détecter la société pré-sélectionnée depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedCompany?: string } | null;
    if (state?.preselectedCompany) {
      setPreselectedCompany(state.preselectedCompany);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && currentUser.email) {
        const role = await getUserRole(currentUser.email);
        setUserRole(role);
      } else {
        setUserRole(null);
      }
    });

    const docRef = doc(db, "prospects", companiesDocId)
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as CompaniesData
        const allOpps: FirestoreOpportunity[] = []

        for (const companyName in data) {
          const companyObj = data[companyName]
          if (companyObj.opportunities && Array.isArray(companyObj.opportunities)) {
            companyObj.opportunities.forEach((opp) => {
              allOpps.push({
                ...opp,
                companyId: opp.companyId || companyName
              })
            })
          }
        }

        setOpportunities(allOpps)
        setCompaniesMap(data)
        
        // Charger les types de demande directement depuis les données
        let types = (data.opportunityTypes as string[]) || [];
        
        // Si pas de types dans opportunityTypes, extraire depuis les opportunités existantes
        if (types.length === 0) {
          const existingTypes = new Set<string>();
          allOpps.forEach(opp => {
            if (opp.type) {
              existingTypes.add(opp.type);
            }
          });
          types = Array.from(existingTypes).sort();
          
          // Sauvegarder automatiquement les types extraits
          if (types.length > 0) {
            updateDoc(docRef, {
              opportunityTypes: types
            }).then(() => {
              console.log("Types extraits et sauvegardés:", types);
            }).catch(error => {
              console.error("Erreur lors de la sauvegarde des types:", error);
            });
          }
        }
        
        console.log("Types chargés dans useEffect:", types);
        setOpportunityTypes(types);
      } else {
        console.log("Le document 'prospects/companies' n'existe pas.")
        setOpportunities([])
        setOpportunityTypes([])
      }
    })

    return () => { unsubscribeAuth(); unsubscribe(); }
  }, [companiesDocId])

  const filteredOpportunities = useMemo(() => {
    const q = searchTerm.toLowerCase()
    return opportunities
      .filter((o) => {
        const oppDate = new Date(o.date)
        const matchMonth = filterMonth ? oppDate.getMonth() + 1 === parseInt(filterMonth) : true
        const matchYear = filterYear ? oppDate.getFullYear() === parseInt(filterYear) : true
        const matchType = filterType ? o.type === filterType : true
        const matchStatus = filterStatus ? (o.status || 'En Cours') === filterStatus : true
        return (
          (o.companyId.toLowerCase().includes(q) ||
            o.contact.toLowerCase().includes(q) ||
            o.type.toLowerCase().includes(q) ||
            o.amount.toString().includes(q)) &&
          matchMonth &&
          matchYear &&
          matchType &&
          matchStatus
        )
      })
      .sort((a, b) => {
        const direction = sortOrder === "asc" ? 1 : -1
        if (sortBy === "date") {
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * direction
        } else if (sortBy === "amount") {
          return ((a.amount ?? 0) - (b.amount ?? 0)) * direction
        } else {
          return a.companyId.localeCompare(b.companyId) * direction
        }
      })
  }, [opportunities, searchTerm, filterMonth, filterYear, filterType, filterStatus, sortBy, sortOrder])

  const totalHT = filteredOpportunities.reduce((acc, o) => acc + (o.amount || 0), 0)
  const totalTTC = filteredOpportunities.reduce((acc, o) => acc + (o.ttc ?? o.amount * 1.2), 0)

  const handleShowModal = () => {
    setEditingOpportunity(null)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false);
    setPreselectedCompany(null);
  }

  const handleSave = async (data: FirestoreOpportunity) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Ajout opportunité",
          `Nouvelle opportunité ajoutée pour ${data.companyId} - Type: ${data.type} - Montant: ${data.amount}€`
        );
      }
    } catch (error) {
      console.error("Erreur lors du logging:", error);
    }
  }

  const handleUpdate = async (oldOpp: FirestoreOpportunity, newOpp: FirestoreOpportunity) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Modification opportunité",
          `Opportunité modifiée pour ${newOpp.companyId} - Type: ${newOpp.type} - Montant: ${newOpp.amount}€`
        );
      }
    } catch (error) {
      console.error("Erreur lors du logging:", error);
    }
  }

  const handleEditOpportunity = (opp: FirestoreOpportunity) => {
    setEditingOpportunity(opp)
    setShowModal(true)
  }

  const handleDeleteOpportunity = async (opp: FirestoreOpportunity) => {
    setOpportunityToDelete(opp)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (opportunityToDelete) {
      try {
        const docRef = doc(db, "prospects", companiesDocId)
        await updateDoc(docRef, {
          [opportunityToDelete.companyId + ".opportunities"]: arrayRemove(opportunityToDelete)
        })
        
        // Log de la suppression
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Suppression opportunité",
            `Opportunité supprimée pour ${opportunityToDelete.companyId} - Type: ${opportunityToDelete.type} - Montant: ${opportunityToDelete.amount}€`
          );
        }
        
        toast.success("Demande supprimée avec succès !")
      } catch (error) {
        console.error("Erreur lors de la suppression :", error)
        toast.error("Une erreur est survenue pendant la suppression.")
      } finally {
        setShowDeleteModal(false)
        setOpportunityToDelete(null)
      }
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setOpportunityToDelete(null)
  }

  const handleExportCSV = () => {
    const headers = [
      "Société",
      "Contact",
      "Type",
      "Qté",
      "PU",
      "Remise (%)",
      "Montant HT",
      "Montant TTC",
      "Date"
    ];

    const rows = filteredOpportunities.map((opp) => [
      opp.companyId,
      opp.contact,
      opp.type,
      opp.qte?.toString() || "",
      opp.pu?.toFixed(2) || "",
      opp.remise !== undefined ? opp.remise.toString() : "",
      opp.amount.toFixed(2),
      (opp.ttc ?? opp.amount * 1.2).toFixed(2),
      new Date(opp.date).toLocaleDateString("fr-FR")
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => cell).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "export_opportunites.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openContactModal = (opp: FirestoreOpportunity) => {
    const companyInfo = companiesMap[opp.companyId]
    setContactModal({
      name: opp.contact ?? "Contact inconnu",
      email: companyInfo?.principalEmail ?? "Non renseigné",
      phone: companyInfo?.principalPhone ?? "Non renseigné",
      fonction: companyInfo?.principalPosition ?? "",
      gender: companyInfo?.principalGender,
      principalContact: companyInfo?.principalContact,
      principalFirstName: companyInfo?.principalFirstName,
    })
  }

  const closeContactModal = () => setContactModal(null)

  const totalPages = Math.ceil(filteredOpportunities.length / itemsPerPage)

  return (
    <div className="opportunites-container" style={isDemoMode ? { paddingTop: '120px' } : undefined}>
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />

      <h2 className="opportunites-title" style={{ color: '#424272' }}>Liste des Demandes :</h2>

      <div className="opportunites-actions-bar">
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            className="add-opportunite-button"
            onClick={handleShowModal}
            style={{ backgroundColor: '#424272' }}
          >
            + Ajouter une demande
          </button>
          {userRole === 'admin' && (
            <button 
              className="add-opportunite-button" 
              onClick={() => setShowTypeDemandeModal(true)}
              style={{ backgroundColor: '#424272' }}
            >
              + Ajouter un type de demande
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="opportunites-search-bar filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Filtrer par type --</option>
            {[...opportunityTypes].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' })).map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>

          <select className="opportunites-search-bar filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">Filtrer par statut --</option>
            <option value="En Cours">En Cours</option>
            <option value="Validé">Validé</option>
            <option value="Annulé">Annulé</option>
          </select>

          <input type="text" className="opportunites-search-bar" placeholder="Rechercher ..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <button type="button" title="Mois précédent" onClick={() => { if (filterMonth === '') return; const m = parseInt(filterMonth); setFilterMonth(String(m === 1 ? 12 : m - 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <select className="opportunites-search-bar" style={{ maxWidth: "130px" }} value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
              <option value="">Afficher Tout</option>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={(i + 1).toString()}>
                  {new Date(0, i).toLocaleString("fr-FR", { month: "long" }).replace(/^\w/, c => c.toUpperCase())}
                </option>
              ))}
            </select>
            <button type="button" title="Mois suivant" onClick={() => { if (filterMonth === '') return; const m = parseInt(filterMonth); setFilterMonth(String(m === 12 ? 1 : m + 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          <select className="opportunites-search-bar" style={{ maxWidth: "120px" }} value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
            <option value="">Afficher Tout</option>
            {[2024, 2025, 2026].map((year) => (
              <option key={year} value={year.toString()}>{year}</option>
            ))}
          </select>

          <select className="opportunites-search-bar" style={{ maxWidth: "160px" }} value={sortBy} onChange={(e) => setSortBy(e.target.value as "date" | "amount" | "alphabetical")}>
            <option value="alphabetical">Alphabétique</option>
            <option value="date">Date de création</option>
            <option value="amount">Montant (HT)</option>
          </select>

          <button 
            style={{ 
              margin: "0 0.5rem", 
              padding: "0.3rem 0.6rem", 
              background: "#424272", 
              color: "white", 
              border: "none", 
              borderRadius: "4px" 
            }} 
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          >
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>

          <select className="opportunites-search-bar" style={{ maxWidth: "130px" }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
            <option value="10">10 par page</option>
            <option value="15">15 par page</option>
            <option value="25">25 par page</option>
            <option value="50">50 par page</option>
          </select>
        </div>
      </div>

      <table className="opportunites-table">
        <thead>
          <tr>
            <th>Nom de la société</th>
            <th>Contact</th>
            <th>Type</th>
            <th>Qté</th>
            <th>PU (€)</th>
            <th>Remise (%)</th>
            <th>Montant HT(€)</th>
            <th>Montant TTC(€)</th>
            <th>Date</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredOpportunities.length === 0 ? (
            <tr><td colSpan={11} style={{ textAlign: "center" }}>Aucune opportunité enregistrée</td></tr>
          ) : (
            filteredOpportunities.map((opp, index) => (
              <tr key={index}>
                <td>{opp.companyId}</td>
                <td>
                  <button onClick={() => openContactModal(opp)} style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}>
                    {opp.contact}
                  </button>
                </td>
                <td>
                  <Tippy content={opp.description || "Aucune description"} placement="top">
                    <span style={{ cursor: "help", textDecoration: "underline dotted" }}>
                      {opp.type} {opp.description && <span style={{ fontSize: "0.9rem", color: "#007bff" }}>ℹ️</span>}
                    </span>
                  </Tippy>
                </td>
                <td>{opp.qte ?? "-"}</td>
                <td>{opp.pu ? `${opp.pu.toFixed(2)} €` : "-"}</td>
                <td>{opp.remise !== undefined ? `${opp.remise} %` : "-"}</td>
                <td>{opp.amount.toFixed(2)} €</td>
                <td>{(opp.ttc ?? opp.amount * 1.2).toFixed(2)} €</td>
                <td>{new Date(opp.date).toLocaleDateString("fr-FR")}</td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    backgroundColor: opp.status === 'Validé' ? '#28a745' : 
                                  opp.status === 'Annulé' ? '#dc3545' : 
                                  opp.status === 'En Cours' ? '#ffc107' : '#6c757d',
                    color: opp.status === 'Validé' ? 'white' : 
                           opp.status === 'Annulé' ? 'white' : 
                           opp.status === 'En Cours' ? 'black' : 'white'
                  }}>
                    {opp.status || 'En Cours'}
                  </span>
                </td>
                <td>
                  <button className="edit-button" onClick={() => handleEditOpportunity(opp)}><i className="bi bi-pencil-square"></i></button>
                  <button className="delete-button" onClick={() => handleDeleteOpportunity(opp)}><i className="bi bi-trash"></i></button>
                  <button 
                    className="edit-button" 
                    onClick={() => navigate('/devis', { state: { preselectedOpportunity: opp } })} 
                    title="Créer un devis pour cette opportunité"
                    style={{ marginLeft: '5px' }}
                  >
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </td>
              </tr>
            ))
          )}
          <tr style={{ fontWeight: "bold", background: "#f8f8f8" }}>
            <td colSpan={6}>Total</td>
            <td>{totalHT.toFixed(2)} €</td>
            <td>{totalTTC.toFixed(2)} €</td>
            <td colSpan={3}></td>
          </tr>
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>← Précédent</button>
          <span>Page {currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Suivant →</button>
        </div>
      )}

      <OpportuniteModal
        show={showModal}
        handleClose={handleCloseModal}
        onSave={handleSave}
        onUpdate={handleUpdate}
        editingOpportunity={editingOpportunity}
        opportunityTypes={opportunityTypes}
        preselectedCompanyId={preselectedCompany}
      />

      <TypeDemandeModal
        show={showTypeDemandeModal}
        handleClose={() => setShowTypeDemandeModal(false)}
        currentTypes={opportunityTypes}
        onTypesUpdated={(newTypes) => {
          console.log("Callback onTypesUpdated appelé avec les types:", newTypes);
          setOpportunityTypes(newTypes);
        }}
      />

      {showDeleteModal && opportunityToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer l'opportunité pour la société "{opportunityToDelete.companyId}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDelete}>Annuler</button>
              <button className="delete-button" onClick={confirmDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {contactModal && (
        <div className="contact-popup-overlay" onClick={closeContactModal}>
          <div className="contact-popup-card" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup-btn" onClick={closeContactModal}>✕</button>
            <div className="contact-popup-content">
              <div className={`contact-avatar ${
                contactModal.gender === "Mme" ? "avatar-female"
                : contactModal.gender === "Mr" ? "avatar-male"
                : "avatar-neutral"
              }`}>
                {((contactModal.principalContact || "").charAt(0) + (contactModal.principalFirstName || "").charAt(0)).toUpperCase()}
              </div>
              <h3>{contactModal.name}</h3>
              {contactModal.fonction && <p className="contact-role">{contactModal.fonction}</p>}
              <hr />
              <div className="contact-info">
                <p><strong>Téléphone :</strong> {contactModal.phone || "Non renseigné"}</p>
                <p><strong>Email :</strong> {contactModal.email || "Non renseigné"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <BillingRecapTable />

      <ToastContainer position="top-right" autoClose={3000} />
      <Footer />
    </div>
  )
}

export default Opportunites
